import { z } from "zod";
import type { PriceList } from "./catalogue.js";
import { Operation } from "./operations.js";
import { IpFamily, PublicIpName } from "./public-ips.js";
import { Labels } from "./ssh-keys.js";

/*
 * Floating IPs: addresses with a home location that can be pointed at any
 * server of the same network zone, running or not, and moved in seconds
 * (failover). Unlike a public IP the server's operating system must be
 * configured to use the address. Billed monthly, IPv4 and IPv6 alike.
 */

export const FloatingIp = z.object({
  id: z.uuid(),
  name: z.string(),
  description: z.string(),
  family: IpFamily,
  /** The address; for IPv6 the routed /64 network, e.g. `2001:db8:1::/64`. */
  address: z.string(),
  /** Home location: where it is billed; it can serve servers of the whole zone. */
  region: z.string(),
  zone: z.string(),
  /** The server it points at, null when unassigned (filled in by the API). */
  server: z.object({ id: z.uuid().nullable(), name: z.string() }).nullable(),
  /** The provider's id of that server, as stored in the resource copy. */
  serverRef: z.string().nullable(),
  reverseDns: z.array(z.object({ ip: z.string(), dnsPtr: z.string() })),
  /** Blocked by the provider (abuse); cannot be used until unblocked. */
  blocked: z.boolean(),
  protection: z.object({ delete: z.boolean() }),
  labels: Labels,
  createdAt: z.iso.datetime(),
});
export type FloatingIp = z.infer<typeof FloatingIp>;

export const FloatingIpDescription = z.string().trim().max(255);

export const CreateFloatingIpRequest = z
  .object({
    name: PublicIpName,
    family: IpFamily,
    /** Home location; taken from the server when `serverId` is given. */
    region: z.string().min(1).max(64).optional(),
    /** Points the new address at this server right away. */
    serverId: z.uuid().optional(),
    description: FloatingIpDescription.default(""),
    labels: Labels.default({}),
  })
  .refine((v) => v.region !== undefined || v.serverId !== undefined, {
    message: "Choose a location or a server",
    path: ["region"],
  });
export type CreateFloatingIpRequest = z.input<typeof CreateFloatingIpRequest>;

export const UpdateFloatingIpRequest = z
  .object({
    name: PublicIpName.optional(),
    description: FloatingIpDescription.optional(),
    labels: Labels.optional(),
  })
  .refine((v) => Object.values(v).some((x) => x !== undefined), "Nothing to change");
export type UpdateFloatingIpRequest = z.infer<typeof UpdateFloatingIpRequest>;

export const AssignFloatingIpRequest = z.object({ serverId: z.uuid() });

export const FloatingIpProtectionRequest = z.object({ delete: z.boolean() });

/** Answer of writes the provider finishes asynchronously. */
export const FloatingIpChange = z.object({ floatingIp: FloatingIp, operation: Operation });
export type FloatingIpChange = z.infer<typeof FloatingIpChange>;

/** Monthly price of a floating IP of this family in its home location; null when not listed. */
export function floatingIpMonthly(
  ip: Pick<FloatingIp, "family" | "region">,
  prices: Pick<PriceList, "floatingIps">,
): number | null {
  const price = prices.floatingIps.find((p) => p.family === ip.family && p.region === ip.region);
  return price ? Number(price.monthly.net) : null;
}
